import { User } from '@models/outseta/outseta';
import { jwtDecode } from 'jwt-decode';

/**
 * Demo mode — used by the Sales/CS demo environment (jooice-demo-server).
 *
 * The demo runs against a mock backend with a synthetic Outseta token. That token is
 * not valid for the real Outseta tenant: the SDK gets a 401 on validate-token, drops
 * the stored token, and `Outseta.getUser()` then stays pending forever — never
 * resolving, never rejecting — so OutsetaProvider would never reach READY and the app
 * would hang on the splash. In demo mode we skip the Outseta SDK entirely and build
 * the session user from the token's own claims.
 *
 * Enabled with VITE_DEMO_MODE=true at build time. It can never turn on for the real
 * product: PRODUCTION_HOSTS is checked at runtime, so even a misbuilt bundle deployed
 * to app.jooice.com behaves normally.
 *
 * Demo mode also switches telemetry off (Sentry, Mixpanel): the demo runs on
 * fabricated accounts, and those events would otherwise land in the real dashboards.
 */

const PRODUCTION_HOSTS = ['app.jooice.com', 'staging.app.jooice.com'];

let announced = false;

export function isDemoMode(): boolean {
  if (import.meta.env.VITE_DEMO_MODE !== 'true') return false;
  if (typeof window !== 'undefined' && PRODUCTION_HOSTS.includes(window.location.hostname)) {
    console.error('[demo-mode] VITE_DEMO_MODE is set on a production host — ignoring it.');
    return false;
  }
  if (!announced) {
    announced = true;
    console.warn('[demo-mode] active — Outseta and telemetry are disabled.');
  }
  return true;
}

interface DemoTokenClaims {
  email?: string;
  name?: string;
  'outseta:accountUid'?: string;
  'outseta:subscriptionUid'?: string;
  'outseta:planUid'?: string;
}

/**
 * Session user for the demo, derived from the token so it stays consistent with whatever
 * scenario the mock backend issued it for. Only the fields the UI actually reads are
 * filled in; the rest of the Outseta shape is padded so consumers can keep their types.
 */
export function buildDemoUser(token: string): User {
  const claims = jwtDecode<DemoTokenClaims>(token);
  const email = claims.email ?? 'demo@jooice.demo';
  const [firstName = 'Demo', lastName = 'User'] = (claims.name ?? 'Demo User').split(' ');
  const now = new Date().toISOString();
  const renewalDate = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString();

  return {
    _objectType: 'Person',
    Email: email,
    FirstName: firstName,
    LastName: lastName,
    FullName: `${firstName} ${lastName}`,
    MailingAddress: null,
    PasswordLastUpdated: now,
    PasswordMustChange: false,
    PhoneMobile: '',
    PhoneWork: '',
    ProfileImageS3Url: '',
    Title: null,
    Timezone: 'UTC',
    Language: 'en',
    IPAddress: '',
    Referer: '',
    UserAgent: '',
    LastLoginDateTime: now,
    OAuthGoogleProfileId: null,
    PersonAccount: null,
    DealPeople: null,
    LeadFormSubmissions: null,
    EmailListPerson: null,
    AccountUids: claims['outseta:accountUid'] ?? 'DEMO-ACC',
    HasLoggedIn: true,
    OAuthIntegrationStatus: 0,
    UserAgentPlatformBrowser: '',
    HasUnsubscribed: false,
    DiscordUser: null,
    IsConnectedToDiscord: false,
    ActivityEventData: null,
    Uid: 'DEMO-PERSON',
    Created: now,
    Updated: now,
    Account: {
      _objectType: 'Account',
      Name: 'Demo Account',
      Uid: claims['outseta:accountUid'] ?? 'DEMO-ACC',
      // 'Subscribing' keeps trial/unsubscribed banners out of the demo.
      AccountStageLabel: 'Subscribing',
      JooiceFlow: 'social',
      CurrentSubscription: {
        Uid: claims['outseta:subscriptionUid'] ?? 'DEMO-SUB',
        BillingRenewalTerm: 1,
        RenewalDate: renewalDate,
        Plan: { Uid: claims['outseta:planUid'] ?? 'DEMO-PLAN', Name: 'Jooice Demo' },
        SubscriptionAddOns: [],
      },
    },
  } as unknown as User;
}
